import React from "react";
import { useTranslation } from "react-i18next";
import { HeaderMainTitle } from "../Common/HeaderMainTitle";
import { Box, CircularProgress, Drawer, Paper } from "@mui/material";
import { ChartBar } from "@phosphor-icons/react";
import { getClientId } from "../utils";
import { useGetClientAssessments } from "hooks/api/useGetClientAssessements";
import VinelandBanner from "../../ClientDetails/Sections/Assessments/VinelandBanner";
import AverageScore from "../Assessment/AverageScore";
import LinesGraph from "../Assessment/LinesGraph";
import EmptyAssessments from "../../ClientDetails/Sections/Assessments/components/EmptyAssessments";
import PreviousPanel from "../../ClientDetails/Sections/Assessments/components/PreviousPanel";
import { useTheme } from "@mui/material/styles";

interface DrawerAssessmentsProps {
  onBack: () => void;
  user: any;
}

export const DrawerAssessments: React.FC<DrawerAssessmentsProps> = ({ onBack, user }) => {
  const { t } = useTranslation(["clientHeader"]);
  const theme = useTheme();
  const [openPreviousPanel, setOpenPreviousPanel] = React.useState(false);

  const clientId = getClientId(user);
  const { clientAssessments, assessmentsLoading } = useGetClientAssessments(clientId);

  const handleOpenPreviousPanel = () => {
    setOpenPreviousPanel(true);
  };

  const handleClosePreviousPanel = () => {
    setOpenPreviousPanel(false);
  };

  const hasAssessments = clientAssessments && clientAssessments.length > 0;

  const renderContent = () => {
    if (assessmentsLoading) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!hasAssessments) {
      return <EmptyAssessments />;
    }

    return (
      <Box
        sx={{
          padding: "24px",
          overflow: "auto",
          height: "calc(100vh - 120px)",
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.default?.hover || "#f5f5f5",
            borderRadius: theme.radius?.["ex-large"] || "16px",
            padding: theme.spacing(5),
          }}
        >
          <VinelandBanner onClickChartButton={handleOpenPreviousPanel} />
          <AverageScore dataAssessments={clientAssessments} />
          <LinesGraph dataAssessments={[...clientAssessments].reverse()} />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <HeaderMainTitle user={user} title={t("clientHeader:assessments.title")} onBack={onBack} icon={ChartBar} />

      {renderContent()}

      <Drawer
        anchor="right"
        open={openPreviousPanel}
        onClose={handleClosePreviousPanel}
        variant="temporary"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 834,
            padding: theme.spacing(7),
          },
        }}
      >
        <Paper sx={{ position: "static" }} elevation={0}>
          <PreviousPanel
            onClosePanel={handleClosePreviousPanel}
            dataAssessments={clientAssessments}
            clientId={clientId}
          />
        </Paper>
      </Drawer>
    </>
  );
};
